// "Bandeja que se despeja" (video aprobado): al cerrar o archivar, un punto vuela en arco desde la fila hasta su
// contador y la fila se pliega con suavidad. Solo toca el DOM (no cambia datos); respeta "reducir movimiento".
import { reducedMotion } from './spring';

const visible = (el: Element) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
};

// Punto que sale de `desde` y cae en `hacia` describiendo un arco; al llegar, el destino da un pequeño salto.
export function volar(desde: Element, hacia: Element | null, { color = 'var(--ok)', retraso = 0 }: { color?: string; retraso?: number } = {}): Promise<void> {
  if (reducedMotion() || !hacia || !visible(desde) || !visible(hacia) || typeof document.body.animate !== 'function') return Promise.resolve();
  const a = desde.getBoundingClientRect(), b = hacia.getBoundingClientRect();
  const p = document.createElement('div');
  p.className = 'vuelo';
  p.setAttribute('aria-hidden', 'true');
  const x0 = a.left + Math.min(28, a.width / 2), y0 = a.top + a.height / 2;
  p.style.left = `${x0 - 9}px`;
  p.style.top = `${y0 - 9}px`;
  p.style.background = color;
  document.body.append(p);
  const dx = b.left + b.width / 2 - x0, dy = b.top + b.height / 2 - y0;
  const mx = dx * 0.5, my = Math.min(dy, 0) - 70;
  return new Promise((listo) => {
    const anim = p.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: `translate(${mx}px,${my}px) scale(.85)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px,${dy}px) scale(.3)`, opacity: 0.25 },
      ],
      { duration: 650, delay: retraso, easing: 'cubic-bezier(.3,.1,.3,1)', fill: 'backwards' },
    );
    const fin = () => {
      p.remove();
      hacia.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], { duration: 300, easing: 'cubic-bezier(.2,.9,.3,1.3)' });
      listo();
    };
    anim.onfinish = fin;
    anim.oncancel = fin;
  });
}

// Pliega una fila: se desliza a la derecha, se desvanece y su alto baja a cero.
export function plegar(el: HTMLElement): Promise<void> {
  if (reducedMotion() || typeof el.animate !== 'function') return Promise.resolve();
  const h = el.offsetHeight;
  el.style.overflow = 'hidden';
  el.style.pointerEvents = 'none';
  const anim = el.animate(
    [
      { height: `${h}px`, opacity: 1, transform: 'none' },
      { height: `${h}px`, opacity: 0, transform: 'translateX(24px)', offset: 0.45 },
      { height: '0px', opacity: 0, transform: 'translateX(24px)', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', borderTopWidth: '0px', borderBottomWidth: '0px' },
    ],
    { duration: 440, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' },
  );
  return new Promise((listo) => {
    anim.onfinish = () => listo();
    anim.oncancel = () => listo();
  });
}

// Donde cae el punto en la Bandeja de casos: el contador de la vista actual (columna de vistas), o el resumen
// de la lista si la columna esta plegada u oculta.
export function destinoCasos(): Element | null {
  const sel = ['.pm-sub a[aria-current="page"] .cnt', '.pm-sub-plegada .cnt', '.pm-sub a[aria-current="page"]', '.pm-sub-plegada', '.cl-resumen > span', '.lote-n'];
  for (const s of sel) {
    const el = document.querySelector(s);
    if (el && visible(el)) return el;
  }
  return null;
}

// Vuela y pliega las filas de estos casos (como mucho 6 puntos, uno tras otro) y espera a que terminen.
// Si los casos siguen en la lista (p. ej. la vista de resueltos), solo vuela el punto: no se pliega nada.
export async function despejar(ids: string[], hacia: Element | null, { color = 'var(--ok)', pliega = true }: { color?: string; pliega?: boolean } = {}) {
  const filas = ids.map((id) => document.querySelector<HTMLElement>(`[data-caso="${CSS.escape(id)}"]`)).filter((x): x is HTMLElement => !!x);
  const vuelos = filas.filter(visible).slice(0, 6).map((f, i) => volar(f, hacia, { color, retraso: i * 70 }));
  await Promise.all([...vuelos, ...(pliega ? filas.map((f) => plegar(f)) : [])]);
}
